import type { DashboardSettings, HuntType, ResourceKey, UnitKey } from './actionSettings';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { Bot, ChevronDown, Crosshair, Dumbbell, Wheat } from 'lucide-react';
import { useI18n } from '../i18n';

const resources: { key: ResourceKey; label: string }[] = [
  { key: 'hierro', label: 'settings.resources.iron' },
  { key: 'carbon', label: 'settings.resources.coal' },
  { key: 'madera', label: 'settings.resources.wood' },
  { key: 'carne', label: 'settings.resources.meat' }
];

const units: { key: UnitKey; label: string }[] = [
  { key: 'infantry', label: 'settings.units.infantry' },
  { key: 'lancer', label: 'settings.units.lancer' },
  { key: 'marksman', label: 'settings.units.marksman' }
];

type SettingsSectionKey = 'autoBot' | 'collect' | 'training' | 'hunt';

const sectionIcons: Record<SettingsSectionKey, ReactNode> = {
  autoBot: <Bot size={19} />,
  collect: <Wheat size={19} />,
  training: <Dumbbell size={19} />,
  hunt: <Crosshair size={19} />
};

function clamp(value: number, min: number, max: number) {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, Math.round(value)));
}

function NumberStepper({
  label,
  value,
  min,
  max,
  onChange
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}) {
  const { t } = useI18n();
  const current = clamp(value, min, max);
  return (
    <label>
      {label}
      <div className="number-stepper">
        <button type="button" aria-label={`${t('settings.down')} ${label}`} onClick={() => onChange(clamp(current - 1, min, max))}>
          -
        </button>
        <input
          min={min}
          max={max}
          type="number"
          value={current}
          onChange={(event) => onChange(clamp(Number(event.target.value), min, max))}
        />
        <button type="button" aria-label={`${t('settings.up')} ${label}`} onClick={() => onChange(clamp(current + 1, min, max))}>
          +
        </button>
      </div>
    </label>
  );
}

function CollapsibleSettingsSection({
  sectionKey,
  title,
  open,
  onToggle,
  children
}: {
  sectionKey: SettingsSectionKey;
  title: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <section className={`settings-accordion settings-accordion-${sectionKey}`}>
      <button
        type="button"
        className="settings-accordion-trigger"
        aria-label={`Configurar ${title}`}
        aria-expanded={open}
        onClick={onToggle}
      >
        <span className="settings-accordion-icon" aria-hidden="true">
          {sectionIcons[sectionKey]}
        </span>
        <span className="settings-accordion-title">{title}</span>
        <ChevronDown className="settings-accordion-chevron" size={18} aria-hidden="true" />
      </button>
      {open && <div className="settings-accordion-body">{children}</div>}
    </section>
  );
}

export function ActionSettingsPanel({
  settings,
  onChange,
  autoShieldAllowed = true
}: {
  settings: DashboardSettings;
  onChange: (settings: DashboardSettings) => void;
  autoShieldAllowed?: boolean;
}) {
  const { t } = useI18n();
  const [openSections, setOpenSections] = useState<Record<SettingsSectionKey, boolean>>({
    autoBot: true,
    collect: false,
    training: false,
    hunt: false
  });
  function toggleSection(section: SettingsSectionKey) {
    setOpenSections((current) => ({ ...current, [section]: !current[section] }));
  }

  function toggleResource(resource: ResourceKey) {
    const exists = settings.collect.resources.includes(resource);
    const nextResources = exists
      ? settings.collect.resources.filter((item) => item !== resource)
      : [...settings.collect.resources, resource];
    onChange({
      ...settings,
      collect: { ...settings.collect, resources: nextResources.length ? nextResources : ['hierro'] }
    });
  }

  function toggleUnit(unit: UnitKey) {
    const current = settings.training.units;
    const next = { ...current, [unit]: !current[unit] };
    onChange({ ...settings, training: { ...settings.training, units: next } });
  }

  return (
    <div className="settings-grid">
      <CollapsibleSettingsSection
        sectionKey="autoBot"
        title="Auto Bot"
        open={openSections.autoBot}
        onToggle={() => toggleSection('autoBot')}
      >
        <div className="checks">
          <label className="check">
            <input
              type="checkbox"
              checked={settings.autoBotModules.collect}
              onChange={(event) =>
                onChange({
                  ...settings,
                  autoBotModules: { ...settings.autoBotModules, collect: event.target.checked }
                })
              }
            />
            {t('dashboard.collect')}
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={settings.autoBotModules.train_troops}
              onChange={(event) =>
                onChange({
                  ...settings,
                  autoBotModules: { ...settings.autoBotModules, train_troops: event.target.checked }
                })
              }
            />
            Train troops
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={settings.autoBotModules.auto_hunt}
              onChange={(event) =>
                onChange({
                  ...settings,
                  autoBotModules: { ...settings.autoBotModules, auto_hunt: event.target.checked }
                })
              }
            />
            Auto Hunt
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={settings.autoBotHelp}
              onChange={(event) =>
                onChange({
                  ...settings,
                  autoBotHelp: event.target.checked
                })
              }
            />
            Auto Help
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={settings.autoBotHeal}
              onChange={(event) =>
                onChange({
                  ...settings,
                  autoBotHeal: event.target.checked
                })
              }
            />
            Auto Heal
          </label>
          <label className={`check${autoShieldAllowed ? '' : ' check-disabled'}`}>
            <input
              type="checkbox"
              disabled={!autoShieldAllowed}
              checked={autoShieldAllowed ? settings.autoBotShield : false}
              onChange={(event) =>
                onChange({
                  ...settings,
                  autoBotShield: event.target.checked
                })
              }
            />
            Auto Shield
            {!autoShieldAllowed && <span className="check-badge">Pro+</span>}
          </label>
        </div>
      </CollapsibleSettingsSection>

      <CollapsibleSettingsSection
        sectionKey="collect"
        title="Recolectar"
        open={openSections.collect}
        onToggle={() => toggleSection('collect')}
      >
        <div className="checks compact-checks">
          {resources.map((resource) => (
            <label className="check" key={resource.key}>
              <input
                type="checkbox"
                checked={settings.collect.resources.includes(resource.key)}
                onChange={() => toggleResource(resource.key)}
              />
              {t(resource.label as any)}
            </label>
          ))}
        </div>
        <div className="form-grid-2">
          <NumberStepper
            label={t('settings.level')}
            min={1}
            max={10}
            value={settings.collect.level}
            onChange={(level) => onChange({ ...settings, collect: { ...settings.collect, level } })}
          />
          <NumberStepper
            label={t('settings.marches')}
            min={1}
            max={6}
            value={settings.collect.marches}
            onChange={(marches) => onChange({ ...settings, collect: { ...settings.collect, marches } })}
          />
        </div>
      </CollapsibleSettingsSection>

      <CollapsibleSettingsSection
        sectionKey="training"
        title="Train troops"
        open={openSections.training}
        onToggle={() => toggleSection('training')}
      >
        <div className="checks">
          {units.map((unit) => (
            <label className="check" key={unit.key}>
              <input type="checkbox" checked={settings.training.units[unit.key]} onChange={() => toggleUnit(unit.key)} />
              {t(unit.label as any)}
            </label>
          ))}
        </div>
        <label>
          {t('settings.amount')}
          <span className="muted">{settings.training.amountPercent}%</span>
          <input
            min={5}
            max={100}
            step={5}
            type="range"
            value={settings.training.amountPercent}
            onChange={(event) =>
              onChange({
                ...settings,
                training: { ...settings.training, amountPercent: Number(event.target.value) }
              })
            }
          />
        </label>
      </CollapsibleSettingsSection>

      <CollapsibleSettingsSection
        sectionKey="hunt"
        title="Auto Hunt"
        open={openSections.hunt}
        onToggle={() => toggleSection('hunt')}
      >
        <div className="form-grid-2">
          <label>
            {t('settings.type')}
            <select
              value={settings.hunt.huntType}
              onChange={(event) =>
                onChange({
                  ...settings,
                  hunt: {
                    ...settings.hunt,
                    huntType: event.target.value as HuntType,
                    huntLevel: event.target.value === 'terror' ? Math.min(settings.hunt.huntLevel, 20) : settings.hunt.huntLevel
                  }
                })
              }
            >
              <option value="beast">{t('settings.beasts')}</option>
              <option value="terror">{t('settings.terror')}</option>
            </select>
          </label>
          <NumberStepper
            label={t('settings.marches')}
            min={1}
            max={6}
            value={settings.hunt.marches}
            onChange={(marches) => onChange({ ...settings, hunt: { ...settings.hunt, marches } })}
          />
        </div>
        <NumberStepper
          label={t('settings.level')}
          min={1}
          max={settings.hunt.huntType === 'terror' ? 20 : 30}
          value={settings.hunt.huntLevel}
          onChange={(huntLevel) => onChange({ ...settings, hunt: { ...settings.hunt, huntLevel } })}
        />
        <label>
          {t('settings.timer')}
          <select
            value={settings.hunt.rallyMinutes}
            onChange={(event) =>
              onChange({ ...settings, hunt: { ...settings.hunt, rallyMinutes: Number(event.target.value) as 3 | 5 | 10 | 15 } })
            }
          >
            <option value={3}>3 min</option>
            <option value={5}>5 min</option>
            <option value={10}>10 min</option>
            <option value={15}>15 min</option>
          </select>
        </label>
      </CollapsibleSettingsSection>
    </div>
  );
}
